import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import { useAuthStore } from '../stores/auth'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
    meta: { title: 'Không tìm thấy trang', description: 'Trang bạn yêu cầu không tồn tại.' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true, title: 'Tổng quan', description: 'Tổng quan số liệu giao dịch và thống kê theo Hãng.' },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'system-documents',
        path: 'tai-lieu',
        component: () => import('../pages/documentation/SystemDocumentationPage.vue'),
        meta: { permission: 'accounts.view', title: 'Tài liệu hệ thống', description: 'Tra cứu tài liệu và sơ đồ hệ thống.' },
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/profile/ProfilePage.vue'),
        meta: { title: 'Hồ sơ cá nhân', description: 'Cập nhật thông tin cá nhân và đổi mật khẩu.' },
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { title: 'Tổng quan', description: 'Tổng quan số liệu giao dịch và thống kê theo Hãng.' },
      },
      {
        name: 'dealers',
        path: 'dealers',
        component: () => import('../pages/dealers/DealersPage.vue'),
        meta: { permission: 'dealers.view', title: 'Quản lý đại lý', description: 'Quản lý đại lý, User liên kết và Hãng.' },
      },
      {
        name: 'accounts',
        path: 'accounts',
        component: () => import('../pages/accounts/AccountManagementPage.vue'),
        meta: {
          permission: 'accounts.view',
          title: 'Quản lý tài khoản',
          description: 'Quản lý tài khoản đăng nhập, nhóm quyền và trạng thái.',
        },
      },
      {
        name: 'account-permissions',
        path: 'accounts/permissions',
        component: () => import('../pages/accounts/AccountPermissionsPage.vue'),
        meta: {
          permission: 'accounts.permission_group.assign',
          title: 'Phân quyền tài khoản',
          description: 'Gán nhóm quyền cho tài khoản.',
        },
      },
      { name: 'permission-groups', path: 'accounts/permission-groups', component: () => import('../pages/accounts/PermissionGroupsPage.vue'), meta: { permission: 'permission_groups.view', title: 'Quản lý nhóm quyền', description: 'Quản lý nhóm quyền và quyền kế thừa.' } },
      {
        name: 'gift-combos',
        path: 'gift-combos',
        component: () => import('../pages/gifts/GiftCombosPage.vue'),
        meta: { permission: 'gift_combos.view', title: 'Quản lý combo và giá', description: 'Quản lý tên combo, giá và điểm thưởng.' },
      },
      {
        name: 'transaction-import',
        path: 'transactions/import',
        component: () => import('../pages/transactions/TransactionImportPage.vue'),
        meta: {
          permission: 'transactions.import',
          title: 'Nhập giao dịch',
          description: 'Nhập và đối chiếu dữ liệu giao dịch từ các nguồn Excel.',
        },
      },
      {
        name: 'spent-point-transactions',
        path: 'transactions/spent-points',
        component: () => import('../pages/transactions/SpentPointTransactionsPage.vue'),
        meta: { permission: 'transactions.view', title: 'Giao dịch tiêu điểm', description: 'Tra cứu các giao dịch tiêu điểm Showroom đã nhập.' },
      },
      {
        name: 'canceled-spent-point-transactions',
        path: 'transactions/spent-points/canceled',
        component: () => import('../pages/transactions/SpentPointTransactionsPage.vue'),
        meta: { permission: 'transactions.view', title: 'Tiêu điểm đã hủy', description: 'Tra cứu các giao dịch tiêu điểm đã hủy.' },
      },
      {
        name: 'transaction-history',
        path: 'transactions/history',
        component: () => import('../pages/transactions/TransactionHistoryPage.vue'),
        meta: {
          permission: 'transactions.view',
          title: 'Lịch sử nhập giao dịch',
          description: 'Xem lịch sử các lần nhập dữ liệu giao dịch.',
        },
      },
      {
        name: 'consolidated-transactions',
        path: 'transactions/consolidated',
        component: () => import('../pages/transactions/ConsolidatedTransactionsPage.vue'),
        meta: { permission: 'transactions.view', title: 'Giao dịch tích điểm', description: 'Tra cứu các giao dịch tích điểm đã tổng hợp.' },
      },
      {
        name: 'canceled-transactions',
        path: 'transactions/canceled',
        component: () => import('../pages/transactions/ConsolidatedTransactionsPage.vue'),
        meta: {
          permission: 'transactions.view',
          title: 'Giao dịch đã hủy',
          description: 'Tra cứu các giao dịch Showroom đã chuyển sang trạng thái hủy.',
        },
      },
      {
        name: 'gift-transactions',
        path: 'transactions/gifts',
        component: () => import('../pages/transactions/ConsolidatedTransactionsPage.vue'),
        meta: { permission: 'gift_combos.view', title: 'Quà tặng', description: 'Tra cứu các giao dịch quà tặng từ dữ liệu đã nhập.' },
      },
      {
        name: 'transaction-conflicts',
        path: 'transactions/conflicts',
        component: () => import('../pages/transactions/TransactionConflictsPage.vue'),
        meta: {
          permission: 'conflicts.view',
          title: 'Xử lý xung đột giao dịch',
          description: 'Xem và xử lý các xung đột trong dữ liệu đối soát.',
        },
      },
      {
        name: 'transaction-audit-history',
        path: 'transactions/audit-history',
        component: () => import('../pages/transactions/TransactionAuditHistoryPage.vue'),
        meta: { permission: 'audit.view', title: 'Lịch sử thao tác', description: 'Theo dõi lịch sử cập nhật và xử lý dữ liệu.' },
      },
      {
        name: 'transaction-statistics',
        path: 'transactions/statistics',
        component: () => import('../pages/transactions/TransactionStatisticsPage.vue'),
        meta: {
          permission: 'statistics.view',
          title: 'Thống kê giao dịch',
          description: 'Thống kê giao dịch Showroom theo Hãng và khoảng thời gian.',
        },
      },
      {
        name: 'reconciliation-reports',
        path: 'reconciliation-reports',
        component: () => import('../pages/reconciliation/ReconciliationPeriodsPage.vue'),
        meta: {
          permission: 'statistics.view',
          title: 'Biên bản đối soát',
          description: 'Xem trước và xuất biên bản đối soát tích điểm, quà tặng.',
        },
      },
      {
        name: 'reconciliation-period-detail',
        path: 'reconciliation-reports/:periodId',
        component: () => import('../pages/reconciliation/ReconciliationPeriodDetailPage.vue'),
        meta: { permission: 'statistics.view', title: 'Chi tiết kỳ đối soát' },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { title: 'Đăng nhập', description: 'Đăng nhập vào hệ thống đối soát giao dịch.' },
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
        meta: { title: 'Đăng ký', description: 'Tạo tài khoản truy cập hệ thống.' },
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
        meta: { title: 'Khôi phục mật khẩu', description: 'Khôi phục quyền truy cập tài khoản.' },
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
        meta: { title: 'Kiểm tra email', description: 'Kiểm tra email để tiếp tục khôi phục mật khẩu.' },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
    meta: { title: 'Không tìm thấy trang', description: 'Trang bạn yêu cầu không tồn tại.' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

function updateDocumentMeta(to: { meta: Record<string, unknown> }) {
  const title = typeof to.meta.title === 'string' ? to.meta.title : 'Hệ thống đối soát giao dịch'
  const description =
    typeof to.meta.description === 'string' ? to.meta.description : 'Hệ thống quản lý và đối soát giao dịch.'
  document.title = title + ' | Đối soát giao dịch'
  let tag = document.querySelector('meta[name="description"]')
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', 'description')
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', description)
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.initialized) {
    try {
      await auth.restore()
    } catch {
      if (to.name !== 'login') return { name: 'login' }
    }
  }
  if (to.meta.requiresAuth && !auth.user) return { name: 'login' }
  if (to.path.startsWith('/auth') && auth.user) return { name: 'dashboard' }
  if (to.path.startsWith('/auth') && to.name !== 'login') return { name: 'login' }
  if (to.meta.permission && !auth.user?.effective_permissions?.includes(String(to.meta.permission))) return { name: 'dashboard' }
})
router.afterEach((to) => updateDocumentMeta(to))
window.addEventListener('auth:unauthorized', () => {
  const auth = useAuthStore()
  auth.user = null
  if (auth.initialized && router.currentRoute.value.meta.requiresAuth) void router.replace({ name: 'login' })
})
export default router
